import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import { useIntl } from 'react-intl';
import { trainingTypeOptions } from '../../hooks/coach/useCoachProfileForm';
import { CoachProfileEditValues, UseCoachProfileEditDialogResult } from '../../hooks/coach/useCoachProfileEditDialog';

export interface CoachProfileEditDialogProps {
  state: UseCoachProfileEditDialogResult;
  onSaved?: () => void;
  // eslint-disable-next-line no-unused-vars
  onError?: (message: string) => void;
}

/**
 * Modal único para completar/editar el perfil del coach. Reemplaza al wizard
 * `CoachProfileForm` (4 steps con Stripe) en Fase 2 del onboarding.
 * Lo abre el OnboardingChecklist (tarea #1) y el botón "Editar perfil" del
 * header de `/coach/profile`.
 */
export function CoachProfileEditDialog({ state, onSaved, onError }: CoachProfileEditDialogProps) {
  const intl = useIntl();
  const { visible, loading, values, errors, close, setField, save } = state;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const result = await save();
    if (result.success) {
      onSaved?.();
    } else if (result.message) {
      onError?.(result.message);
    }
  };

  const setValue =
    <K extends keyof CoachProfileEditValues>(field: K) =>
    (value: CoachProfileEditValues[K]): void => {
      setField(field, value);
    };

  return (
    <Dialog
      visible={visible}
      onHide={close}
      header={intl.formatMessage({
        id: 'coach.profileDialog.title',
        defaultMessage: 'Completar perfil'
      })}
      modal
      draggable={false}
      resizable={false}
      dismissableMask
      style={{ width: 560, maxWidth: '95vw' }}
      breakpoints={{ '768px': '95vw' }}
    >
      <form onSubmit={handleSubmit} className="p-fluid p-3">
        <div className="field mb-3">
          <label htmlFor="coachName" className="block text-900 font-medium mb-2">
            {intl.formatMessage({ id: 'coach.profileDialog.name', defaultMessage: 'Nombre' })}
          </label>
          <InputText
            id="coachName"
            value={values.name}
            onChange={(e) => setValue('name')(e.target.value)}
            className={errors.name ? 'p-invalid' : ''}
          />
          {errors.name && <small className="p-error">{errors.name}</small>}
        </div>

        <div className="field mb-3">
          <label htmlFor="coachBio" className="block text-900 font-medium mb-2">
            {intl.formatMessage({ id: 'coach.profileDialog.bio', defaultMessage: 'Bio' })}
          </label>
          <InputTextarea
            id="coachBio"
            value={values.bio}
            onChange={(e) => setValue('bio')(e.target.value)}
            rows={3}
            autoResize
          />
        </div>

        <div className="field mb-3">
          <label htmlFor="coachExperience" className="block text-900 font-medium mb-2">
            {intl.formatMessage({
              id: 'coach.profileDialog.experience',
              defaultMessage: 'Experiencia'
            })}
          </label>
          <InputText
            id="coachExperience"
            value={values.experience}
            onChange={(e) => setValue('experience')(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'coach.profileDialog.experiencePlaceholder',
              defaultMessage: '5 años entrenando, certificación X...'
            })}
          />
        </div>

        <div className="field mb-3">
          <label htmlFor="coachSpecialties" className="block text-900 font-medium mb-2">
            {intl.formatMessage({
              id: 'coach.profileDialog.specialties',
              defaultMessage: 'Especialidades'
            })}
          </label>
          <MultiSelect
            inputId="coachSpecialties"
            value={values.trainingSpecialties}
            options={trainingTypeOptions}
            onChange={(e) => setValue('trainingSpecialties')(e.value)}
            optionLabel="label"
            optionValue="value"
            display="chip"
            placeholder={intl.formatMessage({
              id: 'coach.profileDialog.specialtiesPlaceholder',
              defaultMessage: 'Seleccioná una o más'
            })}
          />
        </div>

        <div className="field mb-3 flex align-items-center gap-2">
          <Checkbox inputId="coachHasGym" checked={values.hasGym} onChange={(e) => setValue('hasGym')(!!e.checked)} />
          <label htmlFor="coachHasGym" className="m-0">
            {intl.formatMessage({
              id: 'coach.profileDialog.hasGym',
              defaultMessage: 'Atiendo en un gimnasio'
            })}
          </label>
        </div>

        {values.hasGym && (
          <div className="field mb-3">
            <label htmlFor="coachGymLocation" className="block text-900 font-medium mb-2">
              {intl.formatMessage({
                id: 'coach.profileDialog.gymLocation',
                defaultMessage: 'Ubicación del gimnasio'
              })}
            </label>
            <InputText
              id="coachGymLocation"
              value={values.gymLocation}
              onChange={(e) => setValue('gymLocation')(e.target.value)}
              className={errors.gymLocation ? 'p-invalid' : ''}
            />
            {errors.gymLocation && <small className="p-error">{errors.gymLocation}</small>}
          </div>
        )}

        <div className="field mb-3">
          <label htmlFor="coachEstimatedClients" className="block text-900 font-medium mb-2">
            {intl.formatMessage({
              id: 'coach.profileDialog.estimatedClients',
              defaultMessage: 'Cantidad estimada de alumnos'
            })}
          </label>
          <InputNumber
            inputId="coachEstimatedClients"
            value={values.estimatedClients}
            onValueChange={(e) => setValue('estimatedClients')(typeof e.value === 'number' ? e.value : 0)}
            min={0}
            max={1000}
          />
        </div>

        <div className="flex justify-content-end gap-2 mt-4">
          <Button
            type="button"
            label={intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancelar' })}
            className="p-button-text"
            onClick={close}
            disabled={loading}
          />
          <Button
            type="submit"
            label={intl.formatMessage({ id: 'common.save', defaultMessage: 'Guardar' })}
            loading={loading}
          />
        </div>
      </form>
    </Dialog>
  );
}
